import React, { useEffect, useState } from 'react';
import Masonry from 'react-masonry-css';

import { useDates, useI18n } from '~Hooks';
import { Order as OrderType, Item } from '@baemingo/order-status-events/dist/types';

import ConnectionPopover from './Components/ConnectionPopover';
import { useStorage } from '~Components/Storage';
import Order from './Components/Order';
import HeaderBar from './Components/HeaderBar';

import './kds.css';
import { ItemWithCourseType } from '~Scenes/EventedKitchenDisplay/index';
import { getCompanyData_Company_courseTypes } from '~Scenes/EventedKitchenDisplay/__queries__';
import { GET_ORDER } from '~Queries/orders';
import { useLazyQuery } from '@apollo/client';
import { GetOrder, GetOrder_order, GetOrderVariables } from '~Queries/__queries__';
import { filterOrders } from './utils';
import DueLaterDivider from './Components/DueLaterDivider';
import Heading from '~ComponentsImproved/Typography/Heading';
import { Box } from '~Styles';
import styled, { css } from 'styled-components';
import KdsLite from './Components/KDSLite';
import KdsBottomBar from './Components/BottomBar';
import SummaryPanel from './Components/SummaryPanel';

const Wrapper = styled.div<{ isModal?: boolean }>`
  display: flex;
  flex-direction: column;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  font-weight: 600;
  background-color: #1c232c;
  ${({ isModal }) =>
    isModal &&
    css`
      position: relative;
      width: 100%;
      height: 100%;
    `}
`;

const ContentLayout = styled.div`
  display: flex;
  height: calc(100% - 64px);
  min-height: 0;
`;

const ScrollWrapper = styled.div<{ isKdsLite?: boolean }>`
  flex: 1;
  min-width: 0;
  padding: ${({ isKdsLite }) => (isKdsLite ? '0' : '16px 16px 36px 16px;')};
  overflow: auto;
`;

const Errors = styled.div`
  position: absolute;
  bottom: 100px;
  right: 50px;
  color: var(--colorWhite);
  background: #262626;
`;

const Error = styled.div`
  padding: 15px;
  background: #262626;
  color: var(--colorWhite);
  margin: 10px;
`;

export const OrdersGrid = styled.div<{
  isModal?: boolean;
  showFiveColumns?: boolean;
}>`
  display: grid;
  grid-template-columns: ${({ isModal, showFiveColumns }) =>
    isModal ? 'repeat(3, 1fr)' : showFiveColumns ? 'repeat(5, 1fr)' : 'repeat(4, 1fr)'};
  gap: 20px;

  > div {
    height: fit-content;
  }
`;

const UpcomingSection = styled.div`
  opacity: 0.6;
  margin-top: 24px;
`;

const UpcomingDivider = styled.div`
  border-top: 1px solid var(--colorBlueGray400);
  margin: 24px 0 16px;
  padding-top: 16px;
`;

const UpcomingGrid = styled(OrdersGrid)`
  opacity: 0.85;
`;

const UpcomingTitle = styled(Heading)`
  opacity: 0.9;
`;

export const colors = {
  OK: 'var(--colorUIGreen)',
  ERROR: 'var(--colorUIRed)',
  WARNING: 'var(--colorUIOrange)',
};

/* TODO: Add a comment explaining this type and what order vs. realOrder is */
export interface EnrichedOrder {
  order: { items: Item[] } & OrderType;
  realOrder: GetOrder_order | null;
}

interface Props {
  venueId: string;
  errors: string[];
  loading: boolean;
  progress: number;
  progressText: string | null;
  orders: Array<{ items: Item[] } & OrderType>;
  courseTypes: getCompanyData_Company_courseTypes[];
  itemsWithCourseType: ItemWithCourseType[];
  isModal?: boolean;
}

const KitchenDisplayView = ({
  errors,
  venueId,
  loading,
  progress,
  orders,
  courseTypes,
  itemsWithCourseType,
  isModal,
}: Props) => {
  const { i18n } = useI18n();
  const [storage] = useStorage();
  const dates = useDates();

  const [enrichedOrders, setEnrichedOrders] = useState<EnrichedOrder[]>([]);
  const [fetchOrder] = useLazyQuery<GetOrder, GetOrderVariables>(GET_ORDER);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      const fetchedOrders: EnrichedOrder[] = await Promise.all(
        orders.map(async (order) => {
          if (!order.realId) {
            // Return as if no realOrder
            return {
              order,
              realOrder: null,
            };
          }

          try {
            const { data } = await fetchOrder({ variables: { orderId: order.realId } });
            return {
              order,
              realOrder: data?.order ? { ...data.order } : null,
            };
          } catch (error) {
            return {
              order,
              realOrder: null,
            };
          }
        }),
      );

      setEnrichedOrders(fetchedOrders);
    };

    fetchOrderDetails();
  }, [orders, fetchOrder]);

  const { ordersDayOrMoreAway, ordersDueLaterToday, regularKDSOrders, upcomingOrders } = filterOrders(
    enrichedOrders,
    dates.getEndDate,
  );

  /* Renders a list of EnrichedOrders */
  const renderOrders = (ordersToRender: EnrichedOrder[]) => {
    return ordersToRender.map((enrichedOrder) => (
      <Order
        key={enrichedOrder.order.id}
        order={enrichedOrder.order}
        realOrder={enrichedOrder.realOrder}
        venueId={venueId}
        items={enrichedOrder.order.items}
        courseTypes={courseTypes}
        itemsWithCourseType={itemsWithCourseType}
      />
    ));
  };

  const masonryBreakPoints = storage.showFiveColumns
    ? { default: 5, 1500: 5, 1200: 4, 750: 3, 350: 2 }
    : { default: 4, 1500: 4, 1200: 3, 750: 2, 350: 1 };

  return (
    <Wrapper id="kitchen-display" isModal={isModal}>
      <ConnectionPopover />
      {!storage.KDSLite && (
        <HeaderBar
          loading={{ isLoading: loading, progress }}
          isModal={isModal}
          venueId={venueId}
          ordersDayOrMoreAway={ordersDayOrMoreAway}
          courseTypes={courseTypes}
          itemsWithCourseType={itemsWithCourseType}
        />
      )}
      <ContentLayout>
        <ScrollWrapper isKdsLite={storage.KDSLite}>
        {storage.KDSLite ? (
          <KdsLite
            regularKDSOrders={regularKDSOrders}
            ordersDueLaterToday={ordersDueLaterToday}
            upcomingOrders={upcomingOrders}
            renderOrders={renderOrders}
          />
        ) : storage.showMasonryGrid ? (
          <>
            <Masonry
              breakpointCols={masonryBreakPoints}
              className="kds-grid"
              columnClassName="kds-grid_column">
              {renderOrders(regularKDSOrders)}
            </Masonry>
            {ordersDueLaterToday.length > 0 && (
              <>
                <DueLaterDivider />
                <Masonry
                  breakpointCols={masonryBreakPoints}
                  className="kds-grid"
                  columnClassName="kds-grid_column">
                  {renderOrders(ordersDueLaterToday)}
                </Masonry>
              </>
            )}
            {storage.KDSCourseOrdersMode && upcomingOrders.length > 0 && (
              <UpcomingSection>
                <UpcomingDivider>
                  <UpcomingTitle color="#fff" level={4} bottom={0}>
                    <Box display="flex" alignItems="center" gap="xs">
                      <span>{i18n('KDS.Upcoming') || 'Upcoming'}</span>
                    </Box>
                  </UpcomingTitle>
                </UpcomingDivider>
                <Masonry
                  breakpointCols={masonryBreakPoints}
                  className="kds-grid"
                  columnClassName="kds-grid_column">
                  {renderOrders(upcomingOrders)}
                </Masonry>
              </UpcomingSection>
            )}
          </>
        ) : (
          <>
            <OrdersGrid isModal={isModal} showFiveColumns={storage.showFiveColumns}>
              {renderOrders(regularKDSOrders)}
            </OrdersGrid>
            {ordersDueLaterToday.length > 0 && (
              <>
                <DueLaterDivider />
                <OrdersGrid isModal={isModal} showFiveColumns={storage.showFiveColumns}>
                  {renderOrders(ordersDueLaterToday)}
                </OrdersGrid>
              </>
            )}
            {storage.KDSCourseOrdersMode && upcomingOrders.length > 0 && (
              <UpcomingSection>
                <UpcomingDivider>
                  <UpcomingTitle color="#fff" level={4} bottom={0}>
                    <Box display="flex" alignItems="center" gap="xs">
                      <span>{i18n('KDS.Upcoming') || 'Upcoming'}</span>
                    </Box>
                  </UpcomingTitle>
                </UpcomingDivider>
                <UpcomingGrid isModal={isModal} showFiveColumns={storage.showFiveColumns}>
                  {renderOrders(upcomingOrders)}
                </UpcomingGrid>
              </UpcomingSection>
            )}
          </>
        )}
        </ScrollWrapper>
        {storage.KDSSummaryPanel && !storage.KDSLite && (
          <SummaryPanel
            allOrders={enrichedOrders}
            regularKDSOrders={regularKDSOrders}
            upcomingOrders={upcomingOrders}
            courseTypes={courseTypes}
            itemsWithCourseType={itemsWithCourseType}
          />
        )}
      </ContentLayout>

      {errors.length > 0 && (
        <Errors>
          {i18n('KDS.ERROR')}
          {errors.map((error) => (
            <Error>{error}</Error>
          ))}
        </Errors>
      )}

      <KdsBottomBar venueId={venueId} isModal={isModal} />
    </Wrapper>
  );
};

export default React.memo(KitchenDisplayView);
